import '@alto-avios/alto-tokens/web/avios/fonts.css';
import { clsx } from 'clsx';
import { FC, useContext } from 'react';
import { Section, Box } from '@alto-avios/alto-ui';

import { HEADER } from '../../consts';
import { UserDetailsErrorProvider } from '../../contexts/userDetailsError/UserDetailsErrorProvider';
import { useScreenWidth } from '../../hooks/useScreenWidth';
import { DesktopBannerContent } from '../DesktopBannerContent';
import { ErrorBoundary } from '../ErrorBoundary';
import { Loader } from '../Loader';
import { Logo } from '../Logo';
import { MaintenanceBanner } from '../MaintenanceBanner';
import { Navigation } from '../Navigation';
import { CMSProviderContext } from '../services/content';
import { SkipLink } from '../SkipLink';
import { UserDetails } from '../UserDetails';
import { usePopoverRelativeElement } from '../Popover';
import { Modal } from '../Modal';
import { useVersioningDataProps } from '../../hooks/useVersioningDataProps';

import styles from './Header.module.scss';
import { HeaderProps } from './Header.props';

export const Header: FC<HeaderProps> = ({
  auth,
  hideMaintenanceBanner,
  hideModals,
  isLoading,
  skipTo,
  userBalance,
  userBalanceError,
  userDetails,
  userDetailsError,
}) => {
  const { header, maintenanceBanners } = useContext(CMSProviderContext);
  const headerIsLoading = isLoading || header.isFetching;
  const isSmallScreenSize = useScreenWidth() < 1024;
  const { logo, signInLabel, programmeLabel, programmeLogo } = header.data;
  const hasActiveBanners = maintenanceBanners?.data?.[0] !== undefined;
  const shouldDisplayMaintenanceBanner = !headerIsLoading && !hideMaintenanceBanner && hasActiveBanners;
  const popoverRelativeElement = usePopoverRelativeElement<HTMLDivElement>();
  const versioningDataProps = useVersioningDataProps();

  const renderNavSection = () => {
    if (headerIsLoading) {
      return (
        <>
          <span data-testid="nav-left-mobile-loader" className={clsx(styles.displayOnMobile)}>
            <Loader className={clsx(styles.mobileLogoSkeleton)} height={32} width={96} />
          </span>
          <span data-testid="nav-left-desktop-loader" className={clsx(styles.navLeftSkeleton, styles.displayOnDesktop)}>
            <Loader height={48} width={120} className={clsx(styles.displayOnDesktop)} />
            <ul className={clsx(styles.mobileNavSkeleton, styles.displayOnDesktop)}>
              {[...Array(2)].map((_, i) => (
                <li key={i}>
                  <Loader height={48} width={140} />
                </li>
              ))}
            </ul>
          </span>
        </>
      );
    }

    return (
      <>
        <Logo imageData={logo} variant={HEADER} />
        {!isSmallScreenSize && <Navigation variant="header" />}
      </>
    );
  };

  return (
    <ErrorBoundary>
      {!hideModals && <Modal />}
      <header className={styles.header} data-testid="header" {...versioningDataProps}>
        <SkipLink skipTo={skipTo} />

        {!isSmallScreenSize && <DesktopBannerContent isLoading={headerIsLoading} programmeLabel={programmeLabel} programmeLogo={programmeLogo} />}

        <Section className={styles.headerSection} paddingX="xs" paddingY={null}>
          <Section.Container className={styles.headerContainer}>
            <Box justifyContent="center" alignItems="center" width="100%">
              <div className={styles.nav} ref={popoverRelativeElement}>
                <div className={styles.navLeft}>{renderNavSection()}</div>
                <div className={styles.navRight} data-testid="nav-right-user-details">
                  <UserDetailsErrorProvider userDetailsError={userDetailsError} userBalanceError={userBalanceError}>
                    <UserDetails
                      auth={auth}
                      userDetails={userDetails}
                      userBalance={userBalance}
                      signInLabel={signInLabel}
                      isLoading={headerIsLoading}
                    />
                  </UserDetailsErrorProvider>
                </div>
              </div>
            </Box>
          </Section.Container>
        </Section>
      </header>
      {shouldDisplayMaintenanceBanner && (
        <div className={styles.maintenanceBannerWrapper}>
          {maintenanceBanners.data.map((banner) => (
            <MaintenanceBanner key={banner.text} {...banner} />
          ))}
        </div>
      )}
    </ErrorBoundary>
  );
};
